<script>
  import { createEventDispatcher, onMount } from 'svelte'
  import { workoutFormMode, editingWorkoutId, startingTemplateId } from '../stores/app.js'
  import { saveWorkoutWithExercises, getWorkoutWithExercises, createDefaultSet } from '../services/db.js'
  import { getTemplates, createWorkoutFromTemplate, updateTemplateUsage, saveTemplateWithExercises, getTemplate } from '../services/templates.js'
  import { format } from 'date-fns'
  import ExerciseInput from './ExerciseInput.svelte'

  const dispatch = createEventDispatcher()

  let workout = {
    id: crypto.randomUUID(),
    date: format(new Date(), 'yyyy-MM-dd'),
    time: format(new Date(), 'HH:mm'),
    type: 'strength',
    duration: '',
    notes: '',
    template_id: null
  }

  let exercises = []
  let isSubmitting = false
  let showTemplateSelector = false
  let templates = []
  let originalTemplateId = null
  let hasModifiedFromTemplate = false

  const workoutTypes = [
    { value: 'strength', label: '重訓', emoji: '💪' },
    { value: 'cardio', label: '有氧', emoji: '🏃' },
    { value: 'flexibility', label: '伸展', emoji: '🧘' },
    { value: 'sports', label: '運動', emoji: '⚽' },
    { value: 'other', label: '其他', emoji: '📋' }
  ]

  onMount(async () => {
    if ($workoutFormMode === 'edit' && $editingWorkoutId) {
      try {
        const data = await getWorkoutWithExercises($editingWorkoutId)
        if (data) {
          const dateObj = new Date(data.workout.date)
          workout = {
            ...data.workout,
            date: format(dateObj, 'yyyy-MM-dd'),
            time: format(dateObj, 'HH:mm'),
            duration: data.workout.duration ? Math.floor(data.workout.duration / 60) : '', // Convert seconds to minutes
            template_id: data.workout.template_id || null
          }
          exercises = data.exercises
          originalTemplateId = workout.template_id
        }
      } catch (error) {
        console.error('Failed to load workout:', error)
        alert('無法載入訓練記錄')
        dispatch('close')
      }
    } else if ($workoutFormMode === 'create' && $startingTemplateId) {
      // Load template when creating workout from template
      try {
        const template = await getTemplate($startingTemplateId)
        if (template) {
          const { workout: templateWorkout, exercises: templateExercises } = await createWorkoutFromTemplate($startingTemplateId)

          // Use template data but keep current date/time
          workout.template_id = $startingTemplateId
          exercises = templateExercises
          originalTemplateId = $startingTemplateId

          showToast(`已載入模板「${template.name}」`)
        }
      } catch (error) {
        console.error('Failed to load template:', error)
        alert('載入模板失敗')
      }
    }
  })

  async function handleShowTemplateSelector() {
    try {
      templates = await getTemplates()
      showTemplateSelector = true
    } catch (error) {
      console.error('Failed to load templates:', error)
      alert('載入模板失敗')
    }
  }

  async function handleSelectTemplate(template) {
    try {
      const { workout: templateWorkout, exercises: templateExercises } = await createWorkoutFromTemplate(template.id)

      // Keep current date/time and other basic info, but use template exercises
      workout.template_id = template.id
      exercises = templateExercises
      originalTemplateId = template.id

      showTemplateSelector = false
      showToast(`已載入模板「${template.name}」`)
    } catch (error) {
      console.error('Failed to load template:', error)
      alert('載入模板失敗')
    }
  }

  function handleCloseTemplateSelector() {
    showTemplateSelector = false
  }

  function handleClose() {
    if (hasUnsavedChanges()) {
      if (!confirm('有尚未儲存的變更，確定要離開嗎？')) {
        return
      }
    }
    dispatch('close')
  }

  function hasUnsavedChanges() {
    if ($workoutFormMode === 'create') {
      return workout.duration || workout.notes || exercises.length > 0
    }
    return true
  }

  function addExercise() {
    exercises = [...exercises, {
      id: crypto.randomUUID(),
      exercise_name: '',
      sets: [createDefaultSet()],
      rest_time: '',
      notes: ''
    }]
  }

  function removeExercise(event) {
    const id = event.detail.id
    const exercise = exercises.find(ex => ex.id === id)
    if (exercise && exercise.exercise_name) {
      if (!confirm(`確定要移除「${exercise.exercise_name}」嗎？`)) {
        return
      }
    }
    exercises = exercises.filter(ex => ex.id !== id)
  }

  async function handleSubmit() {
    if (isSubmitting) return

    // Validate
    if (!workout.date) {
      alert('請選擇日期')
      return
    }

    isSubmitting = true

    try {
      // Combine date and time
      const dateTime = new Date(`${workout.date}T${workout.time || '00:00'}:00`)

      const workoutData = {
        id: workout.id,
        date: dateTime.toISOString(),
        type: workout.type,
        duration: workout.duration ? parseInt(workout.duration, 10) * 60 : null, // Convert minutes to seconds
        notes: workout.notes?.trim() || '',
        template_id: workout.template_id || null,
        createdAt: workout.createdAt || new Date().toISOString()
      }

      // Filter exercises with names and process sets
      const exercisesData = exercises
        .filter(ex => ex.exercise_name?.trim())
        .map(ex => ({
          id: ex.id,
          exercise_name: ex.exercise_name,
          sets: Array.isArray(ex.sets)
            ? ex.sets.map(set => ({
                weight: set.weight ? parseFloat(set.weight) : 0,
                reps: set.reps ? parseInt(set.reps, 10) : 0,
                completed: set.completed || false
              }))
            : [{ weight: 0, reps: 0, completed: false }],
          rest_time: ex.rest_time ? parseInt(ex.rest_time, 10) : null,
          notes: ex.notes || ''
        }))

      await saveWorkoutWithExercises(workoutData, exercisesData)

      // Update template usage statistics if workout was created from template
      if (originalTemplateId) {
        await updateTemplateUsage(originalTemplateId)
      }

      // Show success toast
      showToast($workoutFormMode === 'create' ? '訓練記錄已儲存' : '訓練記錄已更新')

      // If workout was created from template, ask if user wants to update template
      if (originalTemplateId) {
        setTimeout(() => {
          if (confirm('訓練已完成！要用本次訓練更新原模板嗎？')) {
            updateTemplateFromWorkout(originalTemplateId, exercisesData)
          }
        }, 600)
      }

      setTimeout(() => {
        dispatch('save')
      }, 500)

    } catch (error) {
      console.error('Save error:', error)
      alert('儲存失敗，請稍後再試')
      isSubmitting = false
    }
  }

  async function updateTemplateFromWorkout(templateId, exercisesData) {
    try {
      // Convert workout exercises to template exercises
      const templateExercises = exercisesData.map((ex, index) => ({
        exercise_name: ex.exercise_name,
        order_index: index,
        sets: ex.sets.map(set => ({
          weight: set.weight || null,
          reps: set.reps || null,
          completed: false
        })),
        rest_time: ex.rest_time || null,
        notes: ex.notes || ''
      }))

      // Get template to preserve name and description
      const { getTemplate } = await import('../services/templates.js')
      const template = await getTemplate(templateId)
      if (!template) return

      await saveTemplateWithExercises(template, templateExercises)
      showToast('模板已更新')
    } catch (error) {
      console.error('Failed to update template:', error)
      alert('更新模板失敗')
    }
  }

  function showToast(message) {
    const toast = document.createElement('div')
    toast.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 animate-fade-in'
    toast.textContent = `✓ ${message}`
    document.body.appendChild(toast)
    setTimeout(() => toast.remove(), 2000)
  }

  $: notesCount = workout.notes?.length || 0
</script>

<div class="workout-form-container fixed inset-0 bg-white z-50 overflow-y-auto">
  <div class="max-w-2xl mx-auto px-4 py-6">
    <!-- Header -->
    <div class="flex justify-between items-center mb-6">
      <h1 class="text-2xl font-bold text-gray-800">
        {$workoutFormMode === 'create' ? '新增訓練記錄' : '編輯訓練記錄'}
      </h1>
      <button
        type="button"
        class="text-gray-600 hover:text-gray-800 text-2xl font-bold"
        on:click={handleClose}
      >
        ✕
      </button>
    </div>

    <!-- Form -->
    <form on:submit|preventDefault={handleSubmit} class="space-y-6">
      <!-- Basic Info -->
      <h2 class="text-xl font-semibold mb-4 text-gray-800">基本資訊</h2>

      <div class="space-y-4">
        <!-- Date and Time -->
        <div class="grid grid-cols-2 gap-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              日期 <span class="text-red-500">*</span>
            </label>
            <input
              type="date"
              class="input-field w-full"
              bind:value={workout.date}
              required
            />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              時間
            </label>
            <input
              type="time"
              class="input-field w-full"
              bind:value={workout.time}
            />
          </div>
        </div>

        <!-- Workout Type -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">
            訓練類型 <span class="text-red-500">*</span>
          </label>
          <select
            class="input-field w-full"
            bind:value={workout.type}
            required
          >
            {#each workoutTypes as type}
              <option value={type.value}>
                {type.emoji} {type.label}
              </option>
            {/each}
          </select>
        </div>

        <!-- Duration -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">
            訓練時長（分鐘）
          </label>
          <input
            type="number"
            class="input-field w-full"
            placeholder="60"
            bind:value={workout.duration}
            min="1"
          />
        </div>

        <!-- Notes -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">
            備註
          </label>
          <textarea
            class="input-field w-full"
            rows="3"
            placeholder="今天的訓練感覺如何？"
            maxlength="500"
            bind:value={workout.notes}
          ></textarea>
          <div class="text-sm text-gray-500 mt-1">
            {notesCount} / 500 字元
          </div>
        </div>
      </div>

      <!-- Exercises Section -->
      <div class="flex justify-start items-center mb-4 gap-x-4">
        <h2 class="text-xl font-semibold text-gray-800">運動項目</h2>
        <span class="text-sm text-gray-500">選填</span>
      </div>

      <div class="space-y-4">
        {#if exercises.length === 0}
          <div class="text-center py-4">
            <p class="text-gray-500 mb-3">
              尚未新增運動項目
            </p>
            <button
              type="button"
              class="btn btn-secondary mb-2"
              on:click={handleShowTemplateSelector}
            >
              📋 從模板開始
            </button>
          </div>
        {:else}
          {#each exercises as exercise, index (exercise.id)}
            <ExerciseInput
              bind:exercise
              {index}
              on:remove={removeExercise}
            />
          {/each}
        {/if}
      </div>

      <div class="flex gap-2 mt-4">
        <button
          type="button"
          class="btn btn-secondary flex-1"
          on:click={addExercise}
        >
          + 新增運動項目
        </button>
        {#if exercises.length > 0}
          <button
            type="button"
            class="btn btn-secondary"
            on:click={handleShowTemplateSelector}
          >
            📋 載入模板
          </button>
        {/if}
      </div>

      <!-- Form Actions -->
      <div class="sticky bottom-0 bg-white border-t border-gray-200 py-4 -mx-4 px-4">
        <div class="flex gap-4 max-w-2xl mx-auto">
          <button
            type="button"
            class="btn btn-secondary flex-1"
            on:click={handleClose}
          >
            取消
          </button>
          <button
            type="submit"
            class="btn btn-primary flex-1"
            disabled={isSubmitting}
          >
            {isSubmitting ? '儲存中...' : ($workoutFormMode === 'create' ? '儲存' : '更新')}
          </button>
        </div>
      </div>

      <!-- Online/Offline Status -->
      <div class="text-center text-sm text-gray-500">
        {#if navigator.onLine}
          <span class="text-green-600">● 已連線，將自動同步</span>
        {:else}
          <span class="text-orange-600">● 離線模式，將於上線後同步</span>
        {/if}
      </div>
    </form>
  </div>
</div>

<!-- Template Selector Dialog -->
{#if showTemplateSelector}
  <div class="fixed inset-0 bg-black bg-opacity-50 z-[60] flex items-center justify-center p-4">
    <div class="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto">
      <!-- Header -->
      <div class="border-b border-gray-200 p-4 flex justify-between items-center sticky top-0 bg-white">
        <h2 class="text-xl font-semibold text-gray-800">選擇訓練模板</h2>
        <button
          type="button"
          class="text-gray-600 hover:text-gray-800 text-2xl font-bold"
          on:click={handleCloseTemplateSelector}
        >
          ✕
        </button>
      </div>

      <!-- Template List -->
      <div class="p-4 space-y-3">
        {#if templates.length === 0}
          <div class="text-center py-8">
            <p class="text-gray-500">尚無訓練模板</p>
          </div>
        {:else}
          {#each templates as template (template.id)}
            <button
              type="button"
              class="w-full text-left p-4 border border-gray-200 rounded-lg hover:border-primary-500 hover:bg-primary-50 transition-colors"
              on:click={() => handleSelectTemplate(template)}
            >
              <div class="font-semibold text-gray-800 mb-1">{template.name}</div>
              {#if template.description}
                <div class="text-sm text-gray-600 mb-2">{template.description}</div>
              {/if}
              <div class="flex gap-3 text-xs text-gray-500">
                <span>使用次數: {template.use_count || 0}</span>
                {#if template.last_used_at}
                  <span>上次使用: {format(new Date(template.last_used_at), 'yyyy/MM/dd')}</span>
                {/if}
              </div>
            </button>
          {/each}
        {/if}
      </div>
    </div>
  </div>
{/if}
